import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import {
  Key, Plug, Unplug, RefreshCw, AlertCircle, CheckCircle2, Loader2,
  Eye, EyeOff, Apple, Copy, Download, ExternalLink, ChevronRight, ShieldCheck,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

function fmtNum(v) {
  if (v == null) return '–';
  return new Intl.NumberFormat('tr-TR').format(Number(v));
}
function fmtMoney(v, currency = 'USD') {
  if (v == null) return '–';
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(Number(v));
  } catch {
    return `${Number(v).toFixed(2)} ${currency || ''}`.trim();
  }
}
function fmtPct(v) {
  if (v == null) return '–';
  return `${(Number(v) * 100).toFixed(2)}%`;
}

export default function AppleSearchAds() {
  const brandId = localStorage.getItem('currentBrandId');

  const [connection, setConnection] = useState(null);
  const [loadingConnection, setLoadingConnection] = useState(false);

  // Connect form
  const [clientId, setClientId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [orgId, setOrgId] = useState('');
  const [nickname, setNickname] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [accountOptions, setAccountOptions] = useState(null);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [publicKeyPem, setPublicKeyPem] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copiedPublic, setCopiedPublic] = useState(false);

  // UX state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Date range
  const today = new Date();
  const ymd = (d) => d.toISOString().slice(0, 10);
  const defaultFrom = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
  const [fromDate, setFromDate] = useState(ymd(defaultFrom));
  const [toDate, setToDate] = useState(ymd(today));

  // Data
  const [campaigns, setCampaigns] = useState([]);
  const [campaignPerf, setCampaignPerf] = useState([]);
  const [keywordPerf, setKeywordPerf] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'X-Brand-ID': brandId,
  }), [brandId]);

  const fetchConnection = useCallback(async () => {
    if (!brandId) return;
    setLoadingConnection(true);
    try {
      const res = await fetch(`${API_BASE}/api/apple-search-ads/connection`, {
        credentials: 'include',
        headers: headers(),
      });
      const data = await res.json();
      setConnection(data.connection || null);
    } catch (err) {
      console.error('connection fetch failed:', err);
    } finally {
      setLoadingConnection(false);
    }
  }, [brandId, headers]);

  useEffect(() => { fetchConnection(); }, [fetchConnection]);

  const loadCampaigns = useCallback(async () => {
    if (!brandId) return;
    try {
      const res = await fetch(`${API_BASE}/api/apple-search-ads/campaigns`, {
        credentials: 'include',
        headers: headers(),
      });
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('campaigns fetch failed:', err);
    }
  }, [brandId, headers]);

  const loadPerformance = useCallback(async () => {
    if (!brandId || !fromDate || !toDate) return;
    setLoadingData(true);
    try {
      const [campRes, kwRes] = await Promise.all([
        fetch(`${API_BASE}/api/apple-search-ads/campaign-performance?from=${fromDate}&to=${toDate}`, {
          credentials: 'include', headers: headers(),
        }),
        fetch(`${API_BASE}/api/apple-search-ads/keyword-performance?from=${fromDate}&to=${toDate}`, {
          credentials: 'include', headers: headers(),
        }),
      ]);
      const campJson = await campRes.json();
      const kwJson = await kwRes.json();
      setCampaignPerf(campJson.rows || []);
      setKeywordPerf(kwJson.rows || []);
    } catch (err) {
      console.error('performance fetch failed:', err);
      setError(err.message);
    } finally {
      setLoadingData(false);
    }
  }, [brandId, headers, fromDate, toDate]);

  useEffect(() => {
    if (connection?.status === 'ACTIVE') {
      loadCampaigns();
      loadPerformance();
    }
  }, [connection?.status, loadCampaigns, loadPerformance]);

  const submitConnect = async (e) => {
    e?.preventDefault();
    setError(''); setSuccess('');
    if (!clientId || !keyId || !privateKey) {
      setError('Client ID, Key ID ve Private Key zorunlu');
      return;
    }
    setConnecting(true);
    setAccountOptions(null);
    try {
      const res = await fetch(`${API_BASE}/api/apple-search-ads/connect`, {
        method: 'POST',
        credentials: 'include',
        headers: headers(),
        body: JSON.stringify({
          clientId: clientId.trim(),
          keyId: keyId.trim(),
          teamId: teamId.trim() || null,
          privateKey,
          orgId: orgId.trim() || null,
          nickname: nickname.trim() || null,
        }),
      });
      const data = await res.json();
      if (res.status === 409 && data.options) {
        setAccountOptions(data.options);
        setError('Birden fazla org bulundu, lütfen birini seç.');
        return;
      }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccess('Bağlandı.');
      setPrivateKey('');
      await fetchConnection();
    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Apple Ads bağlantısını silmek istediğine emin misin?')) return;
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/apple-search-ads/disconnect`, {
        method: 'DELETE', credentials: 'include', headers: headers(),
      });
      if (!res.ok) throw new Error('Disconnect failed');
      setConnection(null);
      setCampaigns([]); setCampaignPerf([]); setKeywordPerf([]);
      setSuccess('Bağlantı silindi.');
    } catch (err) {
      setError(err.message);
    }
  };

  const runSync = async (kind) => {
    setError(''); setSuccess(''); setSyncing(true);
    try {
      const path = kind === 'metadata'
        ? '/api/apple-search-ads/sync/metadata'
        : kind === 'campaign'
          ? '/api/apple-search-ads/sync/campaign-performance'
          : '/api/apple-search-ads/sync/keyword-performance';
      const body = kind === 'metadata'
        ? null
        : JSON.stringify({ startDate: fromDate, endDate: toDate });
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'POST', credentials: 'include', headers: headers(), body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Sync (${kind}) failed`);
      setSuccess(`Sync (${kind}) tamam: ${JSON.stringify(data)}`);
      await loadCampaigns();
      await loadPerformance();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  if (!brandId) {
    return (
      <div style={{ padding: 24 }}>
        <Card><div style={{ padding: 16 }}>Önce bir brand seçmelisin.</div></Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Apple size={28} />
        <h1 style={{ margin: 0 }}>Apple Search Ads</h1>
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#900', padding: 12, borderRadius: 6, display: 'flex', gap: 8 }}>
          <AlertCircle size={18} /> {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#efe', color: '#063', padding: 12, borderRadius: 6, display: 'flex', gap: 8 }}>
          <CheckCircle2 size={18} /> {success}
        </div>
      )}

      {/* Connection card */}
      <Card>
        <div style={{ padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Bağlantı</h3>
          {loadingConnection ? (
            <div><Loader2 className="spin" /> Yükleniyor…</div>
          ) : connection ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div><b>Status:</b> {connection.status}</div>
              <div><b>Org ID:</b> {connection.orgId}</div>
              <div><b>Client ID:</b> {connection.clientId}</div>
              <div><b>Account:</b> {connection.accountName || connection.nickname || '–'}</div>
              <div><b>Access token expiry:</b> {connection.expiresAt ? new Date(connection.expiresAt).toLocaleString() : '–'}</div>
              <div style={{ marginTop: 8 }}>
                <button onClick={disconnect} style={{ background: '#c33', color: 'white', border: 0, padding: '8px 14px', borderRadius: 4, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <Unplug size={16} /> Bağlantıyı sil
                </button>
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: 760 }}>
              {/* Stepper */}
              <div style={stepBar}>
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} style={{ ...stepDot, ...(wizardStep >= n ? stepDotActive : null) }}>
                    {wizardStep > n ? <CheckCircle2 size={14} /> : n}
                    <span style={{ marginLeft: 6, fontSize: 12 }}>
                      {['Anahtar üret', 'Apple panele yükle', 'Bilgileri yapıştır', 'Bağlan'][n - 1]}
                    </span>
                  </div>
                ))}
              </div>

              {/* STEP 1 — generate keypair */}
              {wizardStep === 1 && (
                <div style={{ display: 'grid', gap: 12 }}>
                  <p style={{ color: '#444', margin: 0 }}>
                    Apple Ads, OAuth değil — kendi P-256 ECDSA anahtar çiftini bizim hesabımız için <b>sen</b> üretmelisin.
                    Aşağıdaki butona basınca tarayıcın üretir; <b>private key tarayıcından dışarı çıkmaz</b>, sadece doğrudan
                    Glowytics formuna doldurulur ve şifreli olarak DB'ye yazılır.
                  </p>
                  <button
                    type="button"
                    disabled={generating}
                    onClick={async () => {
                      setError(''); setGenerating(true);
                      try {
                        const { pemPrivate, pemPublic } = await generateP256KeyPair();
                        setPrivateKey(pemPrivate);
                        setPublicKeyPem(pemPublic);
                        setWizardStep(2);
                      } catch (e) {
                        setError(`Anahtar üretilemedi: ${e.message}`);
                      } finally {
                        setGenerating(false);
                      }
                    }}
                    style={{ ...btnPrimary, width: 'fit-content' }}
                  >
                    {generating ? <Loader2 size={16} className="spin" /> : <ShieldCheck size={16} />} Anahtar çiftini üret
                  </button>
                  <small style={{ color: '#888' }}>
                    Anahtar formatı: P-256 / ES256. Private key tarayıcı belleğinde tutulur, ağa gönderilmez.
                  </small>
                </div>
              )}

              {/* STEP 2 — copy public, open Apple panel */}
              {wizardStep === 2 && (
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: 12, borderRadius: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <b>Public Key (Apple'a yapıştırılacak):</b>
                      <button type="button" onClick={async () => {
                        try { await navigator.clipboard.writeText(publicKeyPem); setCopiedPublic(true); setTimeout(() => setCopiedPublic(false), 1500); } catch { /* ignore */ }
                      }} style={btnSmall}>
                        <Copy size={14} /> {copiedPublic ? 'Kopyalandı' : 'Kopyala'}
                      </button>
                    </div>
                    <textarea readOnly value={publicKeyPem} rows={8}
                      style={{ ...inp, fontFamily: 'monospace', fontSize: 12, background: 'white' }} />
                  </div>

                  <ol style={{ paddingLeft: 20, lineHeight: 1.7 }}>
                    <li>
                      <a href="https://app.searchads.apple.com" target="_blank" rel="noreferrer" style={lnk}>
                        Apple Ads panelini aç <ExternalLink size={12} />
                      </a> ve admin olduğun org'u seç.
                    </li>
                    <li>Sağ üst → <b>Account Settings</b> → sol menü → <b>API</b>.</li>
                    <li><b>Create API User</b> (veya <b>Add</b>) butonuna bas. Bir isim ver (ör. <code>glowytics</code>) ve rol olarak <b>API Account Read Only</b> seç.</li>
                    <li><b>Public Key</b> alanına yukarıdaki public key'in <i>tüm içeriğini</i> yapıştır → <b>Save</b>.</li>
                    <li>Apple sana <b>Client ID</b>, <b>Key ID</b> ve <b>Team ID</b> gösterecek. Üçünü de kopyala — bir sonraki adıma yapıştıracaksın.</li>
                  </ol>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => setWizardStep(1)} style={btnSecondary}>Geri</button>
                    <button type="button" onClick={() => setWizardStep(3)} style={btnPrimary}>
                      Bilgileri yapıştırmaya hazırım <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3 — paste back IDs */}
              {wizardStep === 3 && (
                <div style={{ display: 'grid', gap: 10 }}>
                  <label>Client ID <input value={clientId} onChange={(e) => setClientId(e.target.value)} required style={inp} placeholder="SEARCHADS.xxxxxxxx-..." /></label>
                  <label>Key ID <input value={keyId} onChange={(e) => setKeyId(e.target.value)} required style={inp} /></label>
                  <label>Team ID <input value={teamId} onChange={(e) => setTeamId(e.target.value)} style={inp} placeholder="SEARCHADS.xxxxxxxx-..." /></label>
                  <label>Org ID (boşsa /acls'ten otomatik bulunur) <input value={orgId} onChange={(e) => setOrgId(e.target.value)} style={inp} /></label>
                  <label>Nickname (opsiyonel — bu bağlantıya bir etiket) <input value={nickname} onChange={(e) => setNickname(e.target.value)} style={inp} placeholder="ör. Ana org" /></label>

                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: 12, borderRadius: 6, marginTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <b>Private Key (otomatik dolduruldu — yedek olarak indirebilirsin):</b>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" onClick={() => setShowKey(!showKey)} style={btnSmall}>
                          {showKey ? <><EyeOff size={14} /> Gizle</> : <><Eye size={14} /> Göster</>}
                        </button>
                        <button type="button" onClick={() => downloadFile(`glowytics-apple-ads-${Date.now()}.pem`, privateKey)} style={btnSmall}>
                          <Download size={14} /> Yedek indir
                        </button>
                      </div>
                    </div>
                    <textarea readOnly value={privateKey} rows={showKey ? 10 : 3}
                      style={{ ...inp, fontFamily: 'monospace', fontSize: 12, background: 'white', filter: showKey ? 'none' : 'blur(3px)' }} />
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button type="button" onClick={() => setWizardStep(2)} style={btnSecondary}>Geri</button>
                    <button type="button" onClick={() => setWizardStep(4)} style={btnPrimary}
                      disabled={!clientId.trim() || !keyId.trim() || !privateKey}>
                      Bağlanmaya hazırım <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4 — connect */}
              {wizardStep === 4 && (
                <div style={{ display: 'grid', gap: 12 }}>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: 12, borderRadius: 6 }}>
                    <b>Özet</b>
                    <div style={{ marginTop: 6, fontSize: 13 }}>
                      <div>Client ID: <code>{clientId}</code></div>
                      <div>Key ID: <code>{keyId}</code></div>
                      <div>Team ID: <code>{teamId || '–'}</code></div>
                      <div>Org ID: <code>{orgId || '(otomatik tespit)'}</code></div>
                      <div>Private key: <code>{privateKey ? `${privateKey.length} karakter — tarayıcıda` : 'YOK'}</code></div>
                    </div>
                  </div>

                  <button type="button" disabled={connecting} onClick={submitConnect} style={{ ...btnPrimary, width: 'fit-content' }}>
                    {connecting ? <Loader2 size={16} className="spin" /> : <Plug size={16} />} Bağlan ve test et
                  </button>

                  <button type="button" onClick={() => setWizardStep(3)} style={{ ...btnSecondary, width: 'fit-content' }}>Geri</button>

                  {accountOptions && (
                    <div style={{ background: '#f4f4f4', padding: 10, borderRadius: 6 }}>
                      <div style={{ marginBottom: 6 }}><b>Birden fazla org bulundu, birini seç:</b></div>
                      {accountOptions.map((o) => (
                        <button key={o.orgId} type="button"
                          onClick={() => { setOrgId(String(o.orgId)); setAccountOptions(null); }}
                          style={{ display: 'block', padding: 6, margin: 4, background: 'white', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                          {o.orgName} (id: {o.orgId}, currency: {o.currency})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {connection?.status === 'ACTIVE' && (
        <>
          {/* Sync controls */}
          <Card>
            <div style={{ padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Veri Çekme</h3>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <label>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inp} /></label>
                <label>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inp} /></label>
                <button onClick={() => runSync('metadata')} disabled={syncing} style={btn}>
                  {syncing ? <Loader2 size={16} className="spin" /> : <Key size={16} />} Metadata
                </button>
                <button onClick={() => runSync('campaign')} disabled={syncing} style={btn}>
                  {syncing ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />} Campaign perf
                </button>
                <button onClick={() => runSync('keyword')} disabled={syncing} style={btn}>
                  {syncing ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />} Keyword perf
                </button>
                <button onClick={loadPerformance} disabled={loadingData} style={btn}>
                  {loadingData ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />} Tabloyu yenile
                </button>
              </div>
            </div>
          </Card>

          {/* Campaigns */}
          <Card>
            <div style={{ padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Campaigns ({campaigns.length})</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={tbl}>
                  <thead>
                    <tr>
                      <th style={th}>ID</th><th style={th}>Name</th><th style={th}>Status</th>
                      <th style={th}>Serving</th><th style={th}>App</th>
                      <th style={th}>Daily Budget</th><th style={th}>Total Budget</th><th style={th}>Currency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr key={c.campaign_id}>
                        <td style={td}>{c.campaign_id}</td>
                        <td style={td}>{c.campaign_name}</td>
                        <td style={td}>{c.status}</td>
                        <td style={td}>{c.serving_status}</td>
                        <td style={td}>{c.app_name || c.app_id || '–'}</td>
                        <td style={td}>{fmtMoney(c.daily_budget, c.currency)}</td>
                        <td style={td}>{fmtMoney(c.total_budget, c.currency)}</td>
                        <td style={td}>{c.currency || '–'}</td>
                      </tr>
                    ))}
                    {campaigns.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#888' }}>Henüz veri yok. "Metadata" sync çalıştır.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>

          {/* Campaign performance */}
          <Card>
            <div style={{ padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Campaign Performance ({campaignPerf.length} rows)</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={tbl}>
                  <thead>
                    <tr>
                      <th style={th}>Date</th><th style={th}>Campaign</th>
                      <th style={th}>Impr</th><th style={th}>Taps</th><th style={th}>Installs</th>
                      <th style={th}>TTR</th><th style={th}>CR</th>
                      <th style={th}>Avg CPT</th><th style={th}>Avg CPA</th>
                      <th style={th}>Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignPerf.map((r, i) => (
                      <tr key={`${r.date}-${r.campaign_id}-${i}`}>
                        <td style={td}>{r.date?.slice(0, 10)}</td>
                        <td style={td}>{r.campaign_name || r.campaign_id}</td>
                        <td style={td}>{fmtNum(r.impressions)}</td>
                        <td style={td}>{fmtNum(r.taps)}</td>
                        <td style={td}>{fmtNum(r.installs)}</td>
                        <td style={td}>{fmtPct(r.ttr)}</td>
                        <td style={td}>{fmtPct(r.conversion_rate)}</td>
                        <td style={td}>{fmtMoney(r.avg_cpt, r.currency)}</td>
                        <td style={td}>{fmtMoney(r.avg_cpa, r.currency)}</td>
                        <td style={td}>{fmtMoney(r.local_spend, r.currency)}</td>
                      </tr>
                    ))}
                    {campaignPerf.length === 0 && <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#888' }}>Veri yok.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>

          {/* Keyword performance */}
          <Card>
            <div style={{ padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Keyword Performance ({keywordPerf.length} rows)</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={tbl}>
                  <thead>
                    <tr>
                      <th style={th}>Date</th><th style={th}>Keyword</th><th style={th}>Match</th>
                      <th style={th}>Impr</th><th style={th}>Taps</th><th style={th}>Installs</th>
                      <th style={th}>TTR</th><th style={th}>Avg CPT</th><th style={th}>Avg CPA</th>
                      <th style={th}>Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywordPerf.map((r, i) => (
                      <tr key={`${r.date}-${r.keyword_id}-${i}`}>
                        <td style={td}>{r.date?.slice(0, 10)}</td>
                        <td style={td}>{r.keyword_text || r.keyword_id}</td>
                        <td style={td}>{r.match_type}</td>
                        <td style={td}>{fmtNum(r.impressions)}</td>
                        <td style={td}>{fmtNum(r.taps)}</td>
                        <td style={td}>{fmtNum(r.installs)}</td>
                        <td style={td}>{fmtPct(r.ttr)}</td>
                        <td style={td}>{fmtMoney(r.avg_cpt, r.currency)}</td>
                        <td style={td}>{fmtMoney(r.avg_cpa, r.currency)}</td>
                        <td style={td}>{fmtMoney(r.local_spend, r.currency)}</td>
                      </tr>
                    ))}
                    {keywordPerf.length === 0 && <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#888' }}>Veri yok.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

const inp = { width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', marginTop: 4, boxSizing: 'border-box' };
const btn = { background: '#111', color: 'white', border: 0, padding: '8px 12px', borderRadius: 4, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' };
const btnPrimary = { ...btn, padding: '10px 16px' };
const btnSecondary = { background: 'white', color: '#111', border: '1px solid #ccc', padding: '10px 16px', borderRadius: 4, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' };
const btnSmall = { background: 'white', color: '#111', border: '1px solid #ccc', padding: '4px 8px', borderRadius: 4, display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer', fontSize: 12 };
const lnk = { color: '#0366d6', textDecoration: 'none', display: 'inline-flex', gap: 4, alignItems: 'center' };
const stepBar = { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 };
const stepDot = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '6px 10px', borderRadius: 999, border: '1px solid #ddd', background: '#fafafa', color: '#666', fontSize: 13, minWidth: 28 };
const stepDotActive = { background: '#111', color: 'white', border: '1px solid #111' };
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #eee', background: '#fafafa' };
const td = { padding: '6px 10px', borderBottom: '1px solid #f0f0f0' };

// ── WebCrypto helpers ───────────────────────────────────────────────────────
// Generate a P-256 ECDSA keypair entirely in the browser. The private key
// never leaves the browser tab except as a string the user pastes back into
// the same page's submit handler. Apple accepts PKCS#8 PRIVATE KEY PEM.

async function generateP256KeyPair() {
  if (!window.crypto?.subtle) {
    throw new Error('WebCrypto desteklenmiyor. HTTPS üzerinden modern bir tarayıcı kullanmalısın.');
  }
  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign', 'verify'],
  );
  const [privBuf, pubBuf] = await Promise.all([
    window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey),
    window.crypto.subtle.exportKey('spki', keyPair.publicKey),
  ]);
  return {
    pemPrivate: bufferToPem(privBuf, 'PRIVATE KEY'),
    pemPublic: bufferToPem(pubBuf, 'PUBLIC KEY'),
  };
}

function bufferToPem(buf, label) {
  const b64 = arrayBufferToBase64(buf);
  const lines = b64.match(/.{1,64}/g).join('\n');
  return `-----BEGIN ${label}-----\n${lines}\n-----END ${label}-----\n`;
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
  return window.btoa(str);
}

function downloadFile(filename, contents) {
  const blob = new Blob([contents], { type: 'application/x-pem-file' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
